import clsx from "clsx";
import { type ComponentProps, useEffect } from "react";

import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { useScroll } from "@shared/ui/scroll/scroll";
import { Body } from "@shared/ui/typography/body";

import s from "./card-list-pagination.module.scss";

export type CardListPaginationProps = ComponentProps<"div"> & {
  className?: string;
  totalPages: number;
  currentPageIndex: number;
  onNextClick: () => void;
  onPrevClick: () => void;
  onPaginationClick: (pageNumber: number) => void;
};

export const CardListPagination = (props: CardListPaginationProps) => {
  const {
    className,
    totalPages,
    currentPageIndex,
    onNextClick,
    onPrevClick,
    onPaginationClick,
  } = props;

  const scroll = useScroll();

  useEffect(() => {
    scroll?.scrollTo(0);
  }, [scroll, currentPageIndex]);

  return (
    <div className={clsx(s.root, className)}>
      <Button
        className={clsx(s.navButton, {
          [s.disabled]: currentPageIndex === 1,
        })}
        onClick={onPrevClick}
        aria-label="prev button"
      >
        <Icon name="arrow-left" />
        <Body size="uppercase-small" weight="semiBold">
          назад
        </Body>
      </Button>
      <div className={s.pagination}>
        {Array.from({ length: totalPages }).map((_, idx) => (
          <Button
            onClick={() => onPaginationClick(idx + 1)}
            key={`pagination_btn-${idx + 1}`}
            className={clsx(s.paginationBtn, {
              [s.activePage]: idx + 1 === currentPageIndex,
              [s.disabled]: false,
            })}
            aria-label={`pagination_button-${idx + 1}`}
          >
            <Body weight="semiBold">{idx + 1}</Body>
          </Button>
        ))}
      </div>
      <Button
        className={clsx(s.navButton, {
          [s.disabled]: currentPageIndex === totalPages,
        })}
        onClick={onNextClick}
        aria-label="next button"
      >
        <Body size="uppercase-small" weight="semiBold">
          Вперед
        </Body>
        <Icon name="arrow-right" />
      </Button>
    </div>
  );
};

CardListPagination.displayName = "CardListPagination";
